import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Card, EmptyState, StatTile } from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import { useQueue } from '@/features/queue/api';
import { useNavCounts } from '@/features/shell/api';
import styles from '../DashboardPage.module.css';

/**
 * RL-1675 (gap #75): the nurse dashboard shows only tiles with a data source:
 * `Open chats` (chats from the app waiting for a nurse, the sidebar's
 * `clinicChats`) and `Waiting for vitals` (waiting patients with no vitals
 * yet, from the queue). The always-"—" tiles are gone.
 */
export function NurseDashboard() {
  const { t } = useTranslation();
  const { hasPermission } = useSession();
  const { selected } = useBranch();
  const chats = hasPermission('clinic_chat:read');
  const queue = hasPermission('queue:read');
  const { data: counts } = useNavCounts(chats);
  // Review: only a role that may read the queue asks for it.
  const { data: q } = useQueue(queue);
  const waitingVitals = q?.tickets.filter(
    (x) => x.status === 'waiting' && !x.vitals,
  ).length;
  const scope = selected?.name ?? t('shell.allBranches');
  return (
    <>
      <div className={styles.tiles}>
        {chats && (
          <StatTile
            label={t('nurseDashboard.openChats')}
            value={counts ? (counts.clinicChats ?? 0) : '…'}
            caption={
              <Link to="/patient-chat">
                {t('nurseDashboard.openChatsLink')}
              </Link>
            }
            highlight
          />
        )}
        {queue && (
          <StatTile
            label={t('nurseDashboard.waitingVitals')}
            value={selected ? (waitingVitals ?? '…') : '—'}
            caption={
              selected ? (
                <Link to="/queue">{t('nurseDashboard.waitingVitalsLink')}</Link>
              ) : (
                t('nurseDashboard.chooseBranch')
              )
            }
            highlight={!chats}
          />
        )}
      </div>
      {!chats && !queue && (
        <Card title={`Today · ${scope}`}>
          <EmptyState
            title={t('common.comingSoon')}
            body={t('empty.dashboardValue')}
          />
        </Card>
      )}
    </>
  );
}
